/**
 * Browser-compatible EventEmitter for authentication-related events
 * Used to notify other services (e.g., SignalR) when auth state changes
 */
class SimpleEventEmitter {
  private listeners: Map<string, Array<(...args: any[]) => void>> = new Map();

  on(event: string, handler: (...args: any[]) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(handler);
  }

  off(event: string, handler: (...args: any[]) => void): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    }
  }

  emit(event: string, ...args: any[]): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(...args));
    }
  }
}

export const authEvents = new SimpleEventEmitter();

/**
 * Authentication event types
 */
export const AUTH_EVENTS = {
  /**
   * Emitted when user logs out
   * Payload: { userId: string }
   */
  LOGOUT: 'logout',

  /**
   * Emitted when the active workspace context changes and workspace lists
   * should be refreshed (e.g., after accepting an invitation).
   * Payload: { workspaceId?: string }
   */
  WORKSPACE_CONTEXT_CHANGED: 'workspace-context-changed',
} as const;

export type AuthEventType = typeof AUTH_EVENTS[keyof typeof AUTH_EVENTS];
